/*
 * ifshe Product Options — self-built personalization engine
 * Replaces the Cloudlift "Live Product Options" app (~5MB) with a few KB.
 *
 * Data-driven: reads a JSON config of fields from a product metafield
 * (custom.product_options_config), renders the controls, and enhances them
 * with conditional show/hide, required validation, a live up-charge total,
 * per-option price labels, and an in-circle PHOTO EDITOR (zoom / drag / rotate).
 *
 * Every control submits as a Shopify line item property: name="properties[<Label>]"
 * so the order data + fulfilment flow stay identical to the old app.
 *
 * Photo editor produces TWO things, mirroring the old app's order data:
 *   properties[image]    = the original uploaded file (factory needs the original)
 *   properties[_preview] = the adjusted square-with-circle render (what the customer set up)
 *   properties[_preview_params] = JSON {scale,rotate,x,y} as a redundant fallback
 *
 * Field schema (one object per field in the JSON array):
 *   { key, label, type, required, default, help, placeholder, maxlength,
 *     options:[{value, swatch?, upcharge?}], conditional:{field,equals},
 *     editor:"photo", accept:"image/*" }
 *   type ∈ text | textarea | select | radio | swatch | file
 */
  // British spelling on the UK store (Beck 2026-06-09): word comes from the snippet's
  // data-pers-word; hostname fallback keeps this file a single shared source for both stores.
function persWord(root) {
  return (root && root.dataset && root.dataset.persWord) ||
    (/\.co\.uk$/.test(location.hostname) ? "Personalisation" : "Personalization");
}

(function () {
  "use strict";
  var MOUNTED = "__ifshePoMounted";
  var TAU = Math.PI * 2;

  function money(n, currency) { return (currency || "$") + Number(n).toFixed(2); }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === "class") node.className = attrs[k];
      else if (k === "text") node.textContent = attrs[k];
      else if (k in node && k !== "list") { try { node[k] = attrs[k]; } catch (e) { node.setAttribute(k, attrs[k]); } }
      else node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) {
      if (c == null) return;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return node;
  }

  // ---- Photo editor ----------------------------------------------------
  function buildPhotoEditor(field, name) {
    var fid = "po-file-" + (field.label || "image").replace(/\W+/g, "");
    var canvas = el("canvas", { class: "ifshe-po__editor-canvas", width: 360, height: 360 });
    var fileMain = el("input", { type: "file", id: fid, name: name,
      class: "ifshe-po__editor-file visually-hidden", accept: field.accept || "image/*" });
    var uploadBtn = el("label", { class: "ifshe-po__upload-btn", "for": fid }, [
      el("span", { class: "ifshe-po__upload-txt", text: "Upload Photo" })
    ]);
    var filePrev = el("input", { type: "file", name: "properties[_preview]", class: "ifshe-po__editor-preview", tabindex: "-1" });
    filePrev.setAttribute("hidden", "");
    var paramsInput = el("input", { type: "hidden", name: "properties[_preview_params]", value: "" });
    var tools = el("div", { class: "ifshe-po__editor-tools" }, [
      el("label", { class: "ifshe-po__tool" }, [
        el("span", { class: "ifshe-po__tool-icon", text: "−" }),
        el("input", { type: "range", class: "ifshe-po__range js-po-scale", min: "0.3", max: "4", step: "0.01", value: "1" }),
        el("span", { class: "ifshe-po__tool-icon", text: "+" })
      ]),
      el("div", { class: "ifshe-po__tool-btns" }, [
        el("button", { type: "button", class: "ifshe-po__tool-btn js-po-rotate", text: "⟳ Rotate" }),
        el("button", { type: "button", class: "ifshe-po__tool-btn js-po-reset", text: "Reset" })
      ])
    ]);
    return el("div", { class: "ifshe-po__editor", "data-editor": "photo", "data-shape": field.shape || "circle", "data-required": field.required ? "true" : "false" }, [
      el("div", { class: "ifshe-po__editor-stage" }, [canvas]),
      uploadBtn, fileMain,
      el("p", { class: "ifshe-po__editor-hint", text: "Upload, then drag to position · slider to zoom · rotate sideways photos" }),
      tools, filePrev, paramsInput
    ]);
  }

  // ---- crop shapes (per product: circle / oval / heart / square / rounded) ----
  function shapePath(c, shape, cx, cy, r) {
    if (shape === "oval") {
      // PORTRAIT oval matched to the real locket window (taller than wide, ~1.32 aspect)
      // — was landscape (1.18 x 0.82) which is the wrong orientation for the oval locket
      c.ellipse(cx, cy, r * 0.74, r * 0.98, 0, 0, TAU);
    } else if (shape === "square" || shape === "rounded") {
      var s = r * (shape === "rounded" ? 1.68 : 1.74), x = cx - s / 2, y = cy - s / 2, rad = shape === "rounded" ? s * 0.20 : 0;
      if (rad && c.roundRect) { c.roundRect(x, y, s, s, rad); }
      else { c.rect(x, y, s, s); }
    } else if (shape === "rect") {
      // landscape rounded rectangle — ID-plate photo window (wider than tall, ~1.4)
      var rw = r * 1.90, rh = rw / 1.40, rx = cx - rw / 2, ry = cy - rh / 2, rrad = rh * 0.16;
      if (c.roundRect) { c.roundRect(rx, ry, rw, rh, rrad); }
      else { c.rect(rx, ry, rw, rh); }
    } else if (shape === "rect-tall") {
      // portrait rounded rectangle — 3D crystal/glass photo block (taller than wide, ~1.4)
      var th = r * 1.90, tw = th / 1.40, tx = cx - tw / 2, ty = cy - th / 2, trad = tw * 0.16;
      if (c.roundRect) { c.roundRect(tx, ty, tw, th, trad); }
      else { c.rect(tx, ty, tw, th); }
    } else if (shape === "heart") {
      // plump, rounded heart matched to the real locket silhouette: domed lobes,
      // shallow cleft, ~1:1 aspect, soft bottom point (tuned 2026-06-06 against the product photo)
      var w = r * 0.98, t = cy - r * 0.50, cleft = cy - r * 0.42, b = cy + r * 0.92, lobe = r * 0.52, inx = 0.34;
      c.moveTo(cx, b);
      c.bezierCurveTo(cx - w * 0.55, cy + r * 0.34, cx - w, cy - r * 0.04, cx - w, t);
      c.bezierCurveTo(cx - w, t - lobe, cx - w * inx, t - lobe, cx, cleft);
      c.bezierCurveTo(cx + w * inx, t - lobe, cx + w, t - lobe, cx + w, t);
      c.bezierCurveTo(cx + w, cy - r * 0.04, cx + w * 0.55, cy + r * 0.34, cx, b);
      c.closePath();
    } else {
      c.arc(cx, cy, r, 0, TAU);
    }
  }
  function clipShape(c, shape, cx, cy, r) { c.beginPath(); shapePath(c, shape, cx, cy, r); c.clip(); }

  function setupPhotoEditor(editor) {
    var shape = editor.getAttribute("data-shape") || "circle";
    var canvas = editor.querySelector(".ifshe-po__editor-canvas");
    var ctx = canvas.getContext("2d");
    var W = canvas.width, H = canvas.height, R = Math.min(W, H) / 2 - 8;
    var fileMain = editor.querySelector(".ifshe-po__editor-file");
    var filePrev = editor.querySelector(".ifshe-po__editor-preview");
    var paramsInput = editor.querySelector('input[name="properties[_preview_params]"]');
    var range = editor.querySelector(".js-po-scale");
    var tools = editor.querySelector(".ifshe-po__editor-tools");
    var img = null, scale = 1, rot = 0, ox = 0, oy = 0;
    tools.style.display = "none";

    function paintImage(c, size) {
      var f = size / W;                       // scale display(360) coords up to the export size
      var w = W * f, h = H * f, r = R * f;
      c.save();
      clipShape(c, shape, w / 2, h / 2, r);
      c.fillStyle = "#fff"; c.fillRect(0, 0, w, h);
      if (img) {
        c.translate(w / 2 + ox * f, h / 2 + oy * f);
        c.rotate(rot * Math.PI / 180);
        c.scale(scale * f, scale * f);
        c.drawImage(img, -img.width / 2, -img.height / 2);
      }
      c.restore();
    }
    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#f3ede4"; ctx.fillRect(0, 0, W, H);
      paintImage(ctx, W);
      // dim everything outside the shape so the crop is obvious
      ctx.save();
      ctx.fillStyle = "rgba(253,248,242,0.8)";
      ctx.beginPath(); ctx.rect(0, 0, W, H); shapePath(ctx, shape, W / 2, H / 2, R);
      ctx.fill("evenodd"); ctx.restore();
      ctx.beginPath(); shapePath(ctx, shape, W / 2, H / 2, R);
      ctx.lineWidth = 2; ctx.strokeStyle = "rgba(0,0,0,0.18)"; ctx.stroke();
      if (!img) {
        ctx.fillStyle = "#b3a99a"; ctx.textAlign = "center"; ctx.font = "14px sans-serif";
        ctx.fillText("Your photo preview", W / 2, H / 2);
      }
    }
    function fit() { scale = (R * 2) / Math.min(img.width, img.height); range.value = scale; ox = 0; oy = 0; rot = 0; }

    // export the adjusted render to the hidden file input (so it travels with the order)
    function exportPreview() {
      if (!img) return;
      // export the adjusted preview at high resolution (so the projection art stays crisp)
      var EXP = Math.min(Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height, 1200), 2000);
      var off = document.createElement("canvas"); off.width = EXP; off.height = EXP;
      paintImage(off.getContext("2d"), EXP);
      paramsInput.value = JSON.stringify({ scale: +scale.toFixed(3), rotate: rot, x: Math.round(ox), y: Math.round(oy) });
      try {
        var url = off.toDataURL("image/png");
        var bin = atob(url.split(",")[1]); var arr = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        var dt = new DataTransfer();
        dt.items.add(new File([arr], "adjusted-preview.png", { type: "image/png" }));
        filePrev.files = dt.files;
      } catch (e) { /* Safari w/o DataTransfer: params fallback still recorded */ }
    }
    var t; function schedExport() { clearTimeout(t); t = setTimeout(exportPreview, 180); }

    fileMain.addEventListener("change", function (e) {
      var f = e.target.files && e.target.files[0]; if (!f) return;
      if (f.size > 20 * 1024 * 1024) { alert("That photo is larger than 20MB. Please choose a smaller image so it uploads correctly."); fileMain.value = ""; return; }
      var ut = editor.querySelector(".ifshe-po__upload-txt"); if (ut) ut.textContent = "✓ Photo added — tap to change";
      var rd = new FileReader();
      rd.onload = function (ev) { var im = new Image();
        im.onload = function () { img = im; fit(); tools.style.display = ""; draw(); exportPreview(); };
        im.src = ev.target.result; };
      rd.readAsDataURL(f);
    });
    range.addEventListener("input", function () { scale = parseFloat(range.value); draw(); schedExport(); });
    editor.querySelector(".js-po-rotate").addEventListener("click", function () { rot = (rot + 90) % 360; draw(); schedExport(); });
    editor.querySelector(".js-po-reset").addEventListener("click", function () { if (img) { fit(); draw(); schedExport(); } });

    var dragging = false, sx = 0, sy = 0;
    function pt(e) { var r = canvas.getBoundingClientRect(); var s = e.touches ? e.touches[0] : e;
      return { x: (s.clientX - r.left) * (W / r.width), y: (s.clientY - r.top) * (H / r.height) }; }
    canvas.addEventListener("mousedown", function (e) { if (!img) return; dragging = true; var p = pt(e); sx = p.x; sy = p.y; });
    window.addEventListener("mousemove", function (e) { if (!dragging) return; var p = pt(e); ox += p.x - sx; oy += p.y - sy; sx = p.x; sy = p.y; draw(); });
    window.addEventListener("mouseup", function () { if (dragging) { dragging = false; schedExport(); } });
    canvas.addEventListener("touchstart", function (e) { if (!img) return; dragging = true; var p = pt(e); sx = p.x; sy = p.y; }, { passive: true });
    canvas.addEventListener("touchmove", function (e) { if (!dragging) return; var p = pt(e); ox += p.x - sx; oy += p.y - sy; sx = p.x; sy = p.y; draw(); e.preventDefault(); }, { passive: false });
    canvas.addEventListener("touchend", function () { if (dragging) { dragging = false; schedExport(); } });

    editor._exportSync = exportPreview;
    editor._demo = function (url) { var im = new Image(); im.onload = function () { img = im; fit(); tools.style.display = ""; draw(); exportPreview(); }; im.src = url; };
    editor._state = function () { return { scale: +scale.toFixed(3), rot: rot, ox: Math.round(ox), oy: Math.round(oy), hasPreviewFile: !!(filePrev.files && filePrev.files.length), params: paramsInput.value }; };
    draw();
  }

  // ---- Field renderers -------------------------------------------------
  function renderField(field, root) {
    if (field.type === "intro") return el("p", { class: "ifshe-po__intro", text: field.text || "" });
    // field.key (when present) is the Shopify line-item property name sent to the factory;
    // field.label is purely the shopper-facing display. Falls back to label when no key.
    var name = "properties[" + (field.key || field.label) + "]";
    var wrap = el("div", { class: "ifshe-po__field ifshe-po__field--" + field.type, "data-field": field.label });
    if (field.field_upcharge) wrap.setAttribute("data-field-upcharge", field.field_upcharge);
    if (field.label) {
      var lab = el("label", { class: "ifshe-po__label", text: field.label });
      if (field.required) lab.appendChild(el("span", { class: "ifshe-po__req", text: " *" }));
      if ((field.type === "text" || field.type === "textarea") && field.maxlength) {
        lab.appendChild(el("span", { class: "ifshe-po__label-max", text: " (max " + field.maxlength + " characters)" }));
      }
      if (field.field_upcharge) lab.appendChild(el("span", { class: "ifshe-po__label-price", text: " +" + money(field.field_upcharge, root._currency) }));
      wrap.appendChild(lab);
    }
    if (field.help) wrap.appendChild(el("p", { class: "ifshe-po__help", text: field.help }));

    switch (field.type) {
      case "text":
        wrap.appendChild(el("input", { type: "text", name: name, class: "ifshe-po__input",
          placeholder: field.placeholder || "", maxlength: field.maxlength || 60, value: field.default || "", required: !!field.required }));
        break;
      case "textarea":
        wrap.appendChild(el("textarea", { name: name, class: "ifshe-po__input ifshe-po__textarea",
          placeholder: field.placeholder || "", maxlength: field.maxlength || 200, required: !!field.required, text: field.default || "" }));
        break;
      case "select":
        var sel = el("select", { name: name, class: "ifshe-po__input ifshe-po__select", required: !!field.required });
        // Required select with no preset default: prepend an empty placeholder so the browser
        // doesn't silently auto-select the first option (without it, a shopper could order e.g.
        // the smallest ring size having never opened the dropdown — OE-02 fix 2026-06-14).
        var hasDefault = (field.options || []).some(function (o) { return (field.default || "") === o.value; });
        if (field.required && !hasDefault) {
          sel.appendChild(el("option", { value: "", text: field.placeholder || ("Select " + (field.label || "an option")), selected: true, disabled: true }));
        }
        (field.options || []).forEach(function (o) {
          var opt = el("option", { value: o.value, text: (o.label || o.value) + (o.upcharge ? "  (+" + money(o.upcharge, root._currency) + ")" : "") });
          if (o.upcharge) opt.setAttribute("data-upcharge", o.upcharge);
          if ((field.default || "") === o.value) opt.selected = true;
          sel.appendChild(opt);
        });
        wrap.appendChild(sel);
        break;
      case "radio":
        var rg = el("div", { class: "ifshe-po__radios" });
        (field.options || []).forEach(function (o, i) {
          var id = "po-" + field.label.replace(/\W+/g, "") + "-" + i;
          var inp = el("input", { type: "radio", name: name, id: id, value: o.value, required: !!field.required });
          if ((field.default || "") === o.value) inp.checked = true;
          if (o.upcharge) inp.setAttribute("data-upcharge", o.upcharge);
          rg.appendChild(el("label", { class: "ifshe-po__radio", "for": id }, [
            inp, el("span", { text: o.label || o.value }),
            o.upcharge ? el("span", { class: "ifshe-po__price-tag", text: "+" + money(o.upcharge, root._currency) }) : null
          ]));
        });
        wrap.appendChild(rg);
        break;
      case "swatch":
        var sw = el("div", { class: "ifshe-po__swatches", role: "radiogroup" });
        // no auto-pick of the first option: birthstones must be chosen by the shopper
        var hidden = el("input", { type: "hidden", name: name, value: field.default || "" });
        if (field.required) hidden.setAttribute("data-po-required", "true");
        (field.options || []).forEach(function (o) {
          var active = hidden.value === o.value;
          var kids = [el("span", { class: "ifshe-po__swatch-dot", style: "background:" + (o.swatch || "#ccc") })];
          if (o.upcharge) kids.push(el("span", { class: "ifshe-po__swatch-badge", text: "+" + money(o.upcharge, root._currency) }));
          kids.push(el("span", { class: "ifshe-po__swatch-cap", text: o.label || o.value }));
          var btn = el("button", { type: "button", class: "ifshe-po__swatch" + (active ? " is-active" : ""), title: o.value, "data-value": o.value }, kids);
          if (o.upcharge) btn.setAttribute("data-upcharge", o.upcharge);
          btn.addEventListener("click", function () {
            hidden.value = o.value;
            sw.querySelectorAll(".ifshe-po__swatch").forEach(function (b) { b.classList.remove("is-active"); });
            btn.classList.add("is-active"); recalc(root);
          });
          sw.appendChild(btn);
        });
        wrap.appendChild(sw); wrap.appendChild(hidden);
        break;
      case "image":
        var ig = el("div", { class: "ifshe-po__image-opts", role: "radiogroup" });
        var hiddenI = el("input", { type: "hidden", name: name, value: field.default || (field.options[0] && field.options[0].value) || "" });
        (field.options || []).forEach(function (o) {
          var media = el("span", { class: "ifshe-po__image-opt-media" }, [
            o.image ? el("img", { src: o.image, alt: o.value, loading: "lazy" }) : el("span", { class: "ifshe-po__image-opt-emoji", text: o.emoji || "📦" })
          ]);
          var label = el("span", { class: "ifshe-po__image-opt-label" }, [
            el("span", { class: "ifshe-po__image-opt-name", text: o.label || o.value }),
            el("span", { class: "ifshe-po__image-opt-price", text: o.upcharge ? "+" + money(o.upcharge, root._currency) : "Free" })
          ]);
          var kids = [media, label];
          if (o.caption) kids.push(el("span", { class: "ifshe-po__image-opt-cap", text: o.caption }));
          var card = el("button", { type: "button", class: "ifshe-po__image-opt" + (hiddenI.value === o.value ? " is-active" : ""), "data-value": o.value }, kids);
          if (o.upcharge) card.setAttribute("data-upcharge", o.upcharge);
          card.addEventListener("click", function () {
            hiddenI.value = o.value;
            ig.querySelectorAll(".ifshe-po__image-opt").forEach(function (b) { b.classList.remove("is-active"); });
            card.classList.add("is-active"); recalc(root);
          });
          ig.appendChild(card);
        });
        wrap.appendChild(ig); wrap.appendChild(hiddenI);
        break;
      case "file":
        if (field.editor === "photo") wrap.appendChild(buildPhotoEditor(field, name));
        else {
          var pfid = "po-file-" + field.label.replace(/\W+/g, "");
          wrap.appendChild(el("label", { class: "ifshe-po__upload-btn", "for": pfid }, [el("span", { text: "Upload File" })]));
          wrap.appendChild(el("input", { type: "file", id: pfid, name: name, class: "ifshe-po__file visually-hidden", accept: field.accept || "image/*", required: !!field.required }));
          wrap.appendChild(el("p", { class: "ifshe-po__file-note", text: "JPG/PNG · uploaded securely with your order" }));
        }
        break;
    }
    return wrap;
  }

  // ---- Up-charge total -------------------------------------------------
  function recalc(root) {
    var total = 0;
    root.querySelectorAll("[data-upcharge]").forEach(function (node) {
      var on = node.tagName === "OPTION" ? node.selected
        : node.type === "radio" ? node.checked
        : (node.classList.contains("ifshe-po__swatch") || node.classList.contains("ifshe-po__image-opt")) ? node.classList.contains("is-active") : false;
      if (on) total += parseFloat(node.getAttribute("data-upcharge")) || 0;
    });
    // field-level up-charge (e.g. extra birthstone +$1 — charged once when any value is chosen)
    root.querySelectorAll("[data-field-upcharge]").forEach(function (fe) {
      if (fe.style.display === "none") return;
      var hid = fe.querySelector('input[name^="properties"]');
      if (hid && hid.value && String(hid.value).trim()) total += parseFloat(fe.getAttribute("data-field-upcharge")) || 0;
    });
    var base = parseFloat(root.dataset.basePrice || "0") || 0;
    var up = root.querySelector(".ifshe-po__up-amt"), tot = root.querySelector(".ifshe-po__total-amt");
    if (up) up.textContent = total > 0 ? "+" + money(total, root._currency) : money(0, root._currency);
    if (tot) tot.textContent = money(base + total, root._currency);
    var hid = root.querySelector('input[name="properties[_options_price]"]');
    if (hid) hid.value = total.toFixed(2);
  }

  // ---- Conditional show/hide ------------------------------------------
  function applyConditions(root, fields) {
    fields.forEach(function (f) {
      if (!f.conditional) return;
      var target = root.querySelector('.ifshe-po__field[data-field="' + f.label + '"]');
      if (!target) return;
      var radios = root.querySelectorAll('[name="properties[' + f.conditional.field + ']"]');
      var single = root.querySelector('[name="properties[' + f.conditional.field + ']"]');
      function read() {
        if (radios.length && radios[0].type === "radio") {
          var c = root.querySelector('[name="properties[' + f.conditional.field + ']"]:checked');
          return c ? c.value : "";
        }
        return single ? single.value : "";
      }
      function update() {
        var show = read() === f.conditional.equals;
        target.style.display = show ? "" : "none";
        target.querySelectorAll("input,select,textarea").forEach(function (i) { i.disabled = !show; });
      }
      radios.forEach(function (r) { r.addEventListener("change", update); });
      if (single) single.addEventListener("change", update);
      update();
    });
  }

  // ---- AJAX add-to-cart with an up-charge fee line --------------------
  // The store is on Shopify Basic (no Cart Transform "update" op), so we mirror
  // the old app: add the main product, then add a $0.01 "Product options" line
  // whose quantity = the up-charge in cents (e.g. $21.99 -> qty 2199).
  function currentUpcharge(root) {
    var hid = root.querySelector('input[name="properties[_options_price]"]');
    return hid ? (parseFloat(hid.value) || 0) : 0;
  }
  function feeBreakdown(root) {
    // list each chosen paid option as "Field = X.XX USD" (mirrors old order data)
    var props = {};
    root.querySelectorAll("[data-upcharge]").forEach(function (node) {
      var on = node.tagName === "OPTION" ? node.selected
        : node.type === "radio" ? node.checked
        : (node.classList.contains("ifshe-po__swatch") || node.classList.contains("ifshe-po__image-opt")) ? node.classList.contains("is-active") : false;
      if (!on) return;
      var amt = parseFloat(node.getAttribute("data-upcharge")) || 0;
      if (amt <= 0) return;
      var fieldEl = node.closest(".ifshe-po__field");
      var label = (fieldEl && fieldEl.getAttribute("data-field")) || "Option";
      props[label] = amt.toFixed(2) + " " + (root.dataset.currencyCode || "USD");
    });
    // OE-05: field-level up-charges (Text on Ring +$10, extra birthstone +$5, …) are charged when
    // the field has a value but were missing from this breakdown — so the cart "add-ons" detail
    // didn't sum to the amount actually charged. Include them so detail matches the fee total.
    root.querySelectorAll("[data-field-upcharge]").forEach(function (fe) {
      if (fe.style.display === "none") return;
      var amt = parseFloat(fe.getAttribute("data-field-upcharge")) || 0;
      if (amt <= 0) return;
      var hid = fe.querySelector('input[name^="properties"]');
      if (hid && hid.value && String(hid.value).trim()) {
        props[fe.getAttribute("data-field") || "Option"] = amt.toFixed(2) + " " + (root.dataset.currencyCode || "USD");
      }
    });
    return props;
  }
  function submitWithFee(form, root) {
    var btn = form.querySelector('[name="add"], button[type="submit"], input[type="submit"]');
    if (btn) { btn.disabled = true; btn.setAttribute("aria-busy", "true"); btn.classList.add("ifshe-po-busy"); }
    var qtyInput = form.querySelector('[name="quantity"]');
    var mainQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var upcharge = currentUpcharge(root);
    var feeVariant = parseInt(root.dataset.feeVariant || "0", 10);
    var needFee = upcharge > 0;
    var oid = "po-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

    var fd = new FormData(form);          // keeps the photo file (multipart -> Shopify CDN)
    fd.append("properties[_options_id]", oid);

    function fail(err) {
      if (btn) { btn.disabled = false; btn.removeAttribute("aria-busy"); btn.classList.remove("ifshe-po-busy"); }
      var nt = root.querySelector(".ifshe-po__notice");
      var msg = (err && (err.description || err.message)) || "Sorry, we couldn't add this to your cart. Please try again.";
      if (nt) { nt.textContent = msg; nt.style.display = "block"; nt.scrollIntoView({ behavior: "smooth", block: "center" }); }
      if (window.console) console.error("[ifshe-po] add-to-cart failed", err);
    }

    // CFP-02: a paid option is selected but the $0.01 fee placeholder variant is missing
    // (placeholder product deleted / unpublished / variant id changed). Block BEFORE adding
    // anything — never silently sell a paid-options item at base price (site-wide under-charge).
    if (needFee && !feeVariant) {
      if (window.console) console.error("[ifshe-po] fee variant missing while up-charge>0 — blocking add to avoid undercharge");
      fail({ description: "Sorry, this item can't be added right now — please refresh and try again, or contact us." });
      return;
    }

    fetch("/cart/add.js", { method: "POST", headers: { "Accept": "application/json" }, body: fd })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); })
      .then(function (mainItem) {
        if (!needFee) return null;                          // no paid options -> main line only
        var props = feeBreakdown(root);
        props["_options_id"] = oid;
        props["_options_fee"] = "1";
        return fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: feeVariant, quantity: Math.round(upcharge * 100) * mainQty, properties: props })
        }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); })
          .catch(function (feeErr) {
            // CFP-01: the main item is already in the cart but the fee line failed. Roll the main
            // item back so we NEVER leave a "paid options, base price" order (silent under-charge)
            // or a duplicate on retry. Re-throw so the shopper gets a clear try-again message.
            var rollback = (mainItem && mainItem.key)
              ? fetch("/cart/change.js", {
                  method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
                  body: JSON.stringify({ id: mainItem.key, quantity: 0 })
                }).catch(function () {})
              : Promise.resolve();
            return rollback.then(function () { throw feeErr; });
          });
      })
      .then(function () { window.location.href = "/cart"; })
      .catch(fail);
  }

  // ---- Required validation + export editors on submit -----------------
  function setupValidation(root) {
    var form = root.closest("form");
    if (!form) return;
    // photo upload needs multipart; Shopify's product form is urlencoded by default
    form.setAttribute("enctype", "multipart/form-data");
    form.addEventListener("submit", function (e) {
      root.querySelectorAll('.ifshe-po__editor').forEach(function (ed) { if (ed._exportSync) ed._exportSync(); });
      // not opened yet → expand. If the product has REQUIRED personalization, block this first
      // add-to-cart so the shopper completes it. If everything is OPTIONAL, just expand and let the
      // add proceed on this same click — no pointless second click (OE-08).
      if (root.classList.contains("is-collapsed")) {
        root.classList.remove("is-collapsed");
        var tg = root.querySelector(".ifshe-po__toggle");
        if (tg) { tg.setAttribute("aria-expanded", "true"); var tt = tg.querySelector(".ifshe-po__toggle-txt"); if (tt) tt.textContent = root.dataset.toggleClose || ("Remove " + persWord(root)); }
        var hasRequired = root.querySelector('[required], [data-po-required="true"], .ifshe-po__editor[data-required="true"]');
        if (hasRequired) {
          var nt = root.querySelector(".ifshe-po__notice"); if (nt) { nt.textContent = "Please complete your personalization before adding to cart."; nt.style.display = "block"; }
          e.preventDefault(); e.stopPropagation(); root.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        // optional-only: fall through to the validation + submit path below (adds on this click)
      }
      var bad = null;
      root.querySelectorAll("[required]").forEach(function (i) {
        if (i.disabled) return;
        var ok = i.type === "radio" ? !!root.querySelector('[name="' + i.name + '"]:checked') : !!(i.value && i.value.trim());
        if (!ok && !bad) bad = i;
      });
      // swatch / image-option required: hidden input, not natively validated
      root.querySelectorAll('[data-po-required="true"]').forEach(function (h) {
        if (h.disabled || bad) return;
        if (!(h.value && String(h.value).trim())) {
          var fe = h.closest(".ifshe-po__field");
          bad = (fe && fe.querySelector(".ifshe-po__swatches, .ifshe-po__image-opts")) || h;
        }
      });
      root.querySelectorAll('.ifshe-po__editor[data-required="true"]').forEach(function (ed) {
        var fm = ed.querySelector(".ifshe-po__editor-file");
        if (!fm || fm.disabled) return;
        if (!fm.files || !fm.files.length) { if (!bad) bad = ed; }
      });
      if (bad) {
        e.preventDefault(); e.stopPropagation();
        var fe = bad.closest(".ifshe-po__field"); if (fe) fe.classList.add("ifshe-po__field--error");
        bad.scrollIntoView({ behavior: "smooth", block: "center" }); if (bad.focus) bad.focus();
        return;
      }
      // validation passed -> take over add-to-cart so we can append the fee line
      e.preventDefault(); e.stopPropagation();
      submitWithFee(form, root);
    }, true);
  }

  // ---- Mount -----------------------------------------------------------
  // OE-01: the add button ships disabled+data-po-gate for products with personalization;
  // enabling it here proves the engine ran. If this JS never loads, the button stays disabled
  // so a custom item can't be ordered with zero personalization data (54-necklace black-hole).
  function enableAddButton(root) {
    var f = root.closest("form"); if (!f) return;
    f.querySelectorAll('[name="add"][data-po-gate]').forEach(function (b) { b.disabled = false; });
  }
  function mount(root) {
    if (root[MOUNTED]) return; root[MOUNTED] = true;
    root._currency = root.dataset.currency || "$";
    var cfgEl = root.querySelector(".ifshe-po-config");
    var fields = [];
    try { fields = JSON.parse(cfgEl ? cfgEl.textContent : (root.dataset.config || "[]")); }
    catch (e) { console.warn("[ifshe-po] bad config JSON", e); return; }
    if (!fields.length) { enableAddButton(root); return; }   // no options to enforce -> ungate

    var fieldsWrap = el("div", { class: "ifshe-po__fields" });
    // group consecutive fields sharing the same `group` into one card (Name + Birthstone per heart)
    var curGroup = null, curWrap = null;
    fields.forEach(function (f) {
      if (f.group) {
        if (f.group !== curGroup) {
          curGroup = f.group;
          curWrap = el("div", { class: "ifshe-po__group" });
          if (f.group_label) curWrap.appendChild(el("div", { class: "ifshe-po__group-title", text: f.group_label }));
          fieldsWrap.appendChild(curWrap);
        }
        curWrap.appendChild(renderField(f, root));
      } else {
        curGroup = null; curWrap = null;
        fieldsWrap.appendChild(renderField(f, root));
      }
    });

    var hasUpcharge = fields.some(function (f) { return (f.options || []).some(function (o) { return o.upcharge; }); });
    var pricing = el("div", { class: "ifshe-po__pricing" });
    if (hasUpcharge) {
      pricing.appendChild(el("div", { class: "ifshe-po__pricing-row" }, [el("span", { text: persWord(root) }), el("span", { class: "ifshe-po__up-amt", text: money(0, root._currency) })]));
      pricing.appendChild(el("div", { class: "ifshe-po__pricing-row ifshe-po__pricing-row--total" }, [el("span", { text: "Total" }), el("span", { class: "ifshe-po__total-amt", text: money(root.dataset.basePrice || 0, root._currency) })]));
    }
    root.appendChild(el("input", { type: "hidden", name: "properties[_options_price]", value: "0" }));
    if (cfgEl) cfgEl.style.display = "none";

    // collapsible personalization panel: clean page until the shopper opts in,
    // and they cannot add to cart without opening + completing it
    if (root.dataset.collapsible !== "false") {
      root.classList.add("is-collapsed");
      var toggle = el("button", { type: "button", class: "ifshe-po__toggle", "aria-expanded": "false" },
        [el("span", { class: "ifshe-po__toggle-txt", text: root.dataset.toggleOpen || ("Add " + persWord(root)) })]);
      toggle.addEventListener("click", function () {
        var collapsed = root.classList.toggle("is-collapsed");
        toggle.setAttribute("aria-expanded", String(!collapsed));
        toggle.querySelector(".ifshe-po__toggle-txt").textContent = collapsed
          ? (root.dataset.toggleOpen || ("Add " + persWord(root)))
          : (root.dataset.toggleClose || ("Remove " + persWord(root)));
        var nt = root.querySelector(".ifshe-po__notice"); if (nt) nt.style.display = "none";
      });
      root.appendChild(toggle);
      root.appendChild(el("div", { class: "ifshe-po__notice", role: "alert" }));
    }
    root.appendChild(fieldsWrap); root.appendChild(pricing);

    applyConditions(root, fields);
    root.querySelectorAll(".ifshe-po__editor").forEach(setupPhotoEditor);
    setupValidation(root);
    root.addEventListener("change", function () { recalc(root); });
    recalc(root);

    // OE-01: ungate the add button now the engine has rendered. Re-apply on theme re-renders too
    // (some products re-render the ATC container on variant change, which would restore disabled).
    enableAddButton(root);
    var gateForm = root.closest("form");
    if (gateForm && window.MutationObserver) {
      new MutationObserver(function () { enableAddButton(root); }).observe(gateForm, { childList: true, subtree: true });
    }
  }

  function init() { document.querySelectorAll(".ifshe-po[data-ifshe-options]").forEach(mount); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
  window.IfsheProductOptions = { mount: mount, init: init };
})();
